import React, { useEffect, useState } from 'react';
import { invoiceApi } from '../../firebase/functionsService';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import { useDirectInvoices } from '../../hooks/useDirectInvoices';
import { useInvoiceFeature } from '../../hooks/useInvoiceFeature';
import InvoiceStatusChip from '../common/InvoiceStatusChip';
import { formatKr } from '../../utils/formatKr';
import { COLORS, FONTS } from '../../styles/tokens';

// Fakturaer: utestående beløp. Kreditnotaer: negativt beløp.
function amountOf(inv) {
  return inv.kind === 'credit-note' ? inv.grossAmount : inv.grossAmount - (inv.creditedGross ?? 0);
}

// Sjåførens fakturaer til bestillere (Inntekt-siden)
export default function DriverInvoicesSection() {
  const { enabled } = useInvoiceFeature();
  const { invoices: all, loading } = useDirectInvoices('driver');
  const invoices = all.filter(i => i.kind !== 'tippi-fee'); // gebyr vises i DriverFeeSection
  const [busy, setBusy]       = useState(false); // én handling om gangen
  const [error, setError]     = useState('');
  const [notice, setNotice]   = useState('');
  const [confirm, setConfirm] = useState(null); // faktura som skal merkes betalt
  const [confirmError, setConfirmError] = useState('');

  function closeConfirm() {
    if (busy) return;
    setConfirm(null);
    setConfirmError('');
  }

  useEffect(() => {
    if (!confirm || busy) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setConfirm(null);
        setConfirmError('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirm, busy]);

  if (!enabled && invoices.length === 0) return null;

  async function run(inv, fn, okText) {
    if (busy) return;
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await fn(inv.id);
      if (okText) setNotice(okText);
    } catch (e) {
      setError(e?.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm() {
    if (busy) return;
    const inv = confirm;
    setError('');
    setNotice('');
    setConfirmError('');
    setBusy(true);
    try {
      await invoiceApi.markInvoicePaid(inv.id);
      setConfirm(null);
      setNotice('Fakturaen er merket som betalt.');
    } catch (e) {
      setConfirmError(e?.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={styles.card} aria-label="Fakturaer til bestiller">
      <h2 style={styles.title}>Fakturaer til bestiller</h2>

      {error && <p role="alert" style={styles.error}>{error}</p>}

      {notice && <p role="status" style={styles.notice}>{notice}</p>}

      {loading && invoices.length === 0 && <p style={styles.empty}>Laster fakturaer…</p>}
      {!loading && invoices.length === 0 && <p style={styles.empty}>Ingen fakturaer ennå.</p>}

      {invoices.map(inv => {
        const isNote = inv.kind === 'credit-note';
        const canPay = !isNote && (inv.status === 'issued' || inv.status === 'overdue');
        const canResend = isNote || inv.status === 'issued' || inv.status === 'overdue';
        const failed = inv.mailStatus === 'failed';
        const due = inv.dueDate?.toDate?.();
        return (
          <div key={inv.id} style={styles.row} data-testid={`inv-${inv.id}`}>
            <div style={styles.top}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.number}>{isNote ? 'Kreditnota' : 'Faktura'} {inv.invoiceNumber}</div>
                <div style={styles.meta}>{inv.recipient?.name}</div>
                {!isNote && due && (
                  <div style={styles.meta}>
                    Forfall {due.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={styles.amount} data-testid={`amount-${inv.id}`}>{formatKr(amountOf(inv))}</div>
                <InvoiceStatusChip invoice={inv} />
              </div>
            </div>
            {failed && <span style={styles.failed}>E-post feilet</span>}
            <div style={styles.actions}>
              {canPay && (
                <button type="button" style={styles.primaryBtn} disabled={busy}
                        aria-label={`Merk faktura ${inv.invoiceNumber} som betalt`}
                        onClick={() => setConfirm(inv)}>Merk som betalt</button>
              )}
              <button type="button" style={styles.btn} disabled={busy}
                      onClick={() => run(inv, downloadInvoicePdf)}>Last ned PDF</button>
              {canResend && (
                <button type="button" style={failed ? styles.primaryBtn : styles.btn} disabled={busy}
                        onClick={() => run(inv, invoiceApi.resendInvoiceMail, 'E-posten er sendt på nytt.')}>Send på nytt</button>
              )}
            </div>
          </div>
        );
      })}

      {confirm && (
        <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) closeConfirm(); }}>
          <div role="dialog" aria-modal="true" aria-label="Bekreft betaling" style={styles.dialog}>
            <p style={{ margin: '0 0 16px', fontSize: 15, color: COLORS.textDark }}>
              Merk faktura {confirm.invoiceNumber} som betalt? Dette kan ikke angres.
            </p>
            {confirmError && <p role="alert" style={styles.error}>{confirmError}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" style={styles.btn} autoFocus disabled={busy} onClick={closeConfirm}>Avbryt</button>
              <button type="button" style={styles.primaryBtn} disabled={busy} onClick={handleConfirm}>Merk som betalt</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const btnBase = { borderRadius: 20, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const styles = {
  card:       { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: 18, marginBottom: 14 },
  title:      { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, margin: '0 0 4px', color: COLORS.textDark },
  error:      { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '8px 0' },
  notice:     { background: COLORS.successBg, color: COLORS.successText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '8px 0' },
  empty:      { fontSize: 14, color: COLORS.textLight, margin: '8px 0 0' },
  row:        { padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` },
  top:        { display: 'flex', alignItems: 'flex-start', gap: 12 },
  number:     { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  meta:       { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  amount:     { fontFamily: FONTS.display, fontWeight: 700, fontSize: 22, lineHeight: 1, marginBottom: 5, color: COLORS.textDark },
  failed:     { display: 'inline-block', marginTop: 8, borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, background: COLORS.dangerBg, color: COLORS.dangerText },
  actions:    { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  btn:        { ...btnBase, background: COLORS.white, border: `1px solid ${COLORS.border}`, color: COLORS.textDark },
  primaryBtn: { ...btnBase, background: COLORS.accent, border: 'none', color: '#1a1a2e' },
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  dialog:     { background: COLORS.white, borderRadius: 20, padding: 20, maxWidth: 360, width: '100%' },
};
